<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>PROTO</span>
            </span>
        </div>
    </Transition>

    <v-form :name="formProps.formName"
            @keyup.enter="autoSave = false">
        <Tabs value="0"
              scrollable>

            <TabList>
                <Tab value="0">{{ __('Basic data') }}</Tab>
                <Tab value="1">{{ __('Attendances') }}</Tab>
                <Tab value="2">{{ __('Changelog') }}</Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="0">
                    <div class="grid">
                        <FormInputText fieldName="company_name"
                                       label="Company name"
                                       size="12"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.company_name" />
                    </div>
                    <div class="grid">
                        <FormSelect fieldName="service_id"
                                    label="Service name"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.services"
                                    v-model:field="crud.service_id" />
                    </div>
                    <div class="grid">
                        <FormInputText fieldName="agreement_number"
                                       label="Agreement number"
                                       size="2"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.agreement_number" />

                        <FormInputText fieldName="event_name"
                                       label="Event name"
                                       size="10"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.event_name" />
                    </div>
                    <div class="grid">
                        <FormCalendar fieldName="event_date"
                                      label="Event date"
                                      size="2"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.event_date" />

                        <FormInputText fieldName="location"
                                       label="Location"
                                       size="10"
                                       validation="required"
                                       :isFormDisabled="isFormDisabled"
                                       :isFocus="true"
                                       v-model:field="crud.location" />
                    </div>
                    <div class="grid">
                        <FormTextarea fieldName="agenda"
                                      label="Agenda"
                                      style="margin-bottom:5px"
                                      :keyup="agendaWasChanged"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.agenda" />
                    </div>
                    <div class="grid"
                         style="margin-bottom:15px">
                        <span>{{ crud.agenda?.length ?? 0 }} / {{ maxAgendaChars }}</span>
                    </div>

                    <div class="grid">
                        <FormFileUploader fieldName="files"
                                          label="Pictures"
                                          category="pictures"
                                          :allowedExtensions="['jpg', 'jpeg', 'png', 'pdf']"
                                          :maxFileSize="(props.customData?.maxFileSizeMB ?? 0) * 1024 * 1024"
                                          :maxTotalSize="(props.customData?.maxTotalFileSizeMB ?? 0) * 1024 * 1024"
                                          :isFormDisabled="isFormDisabled"
                                          v-model:field="crud.files" />
                    </div>

                    <div class="grid">
                        <FormInputText fieldName="signatory_partner"
                                       label="Signatory Partner"
                                       size="6"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.signatory_partner" />

                        <FormInputText fieldName="signatory_client"
                                       label="Signatory Client"
                                       size="6"
                                       :isFormDisabled="isFormDisabled"
                                       v-model:field="crud.signatory_client" />
                    </div>
                </TabPanel>
                <TabPanel value="1">
                    <div class="grid">
                        <FormRepeaterTable v-model:record="crud.attendances"
                                           :keys="{ 'first_name': null, 'last_name': null, 'organization': null }"
                                           :isFirstRowEmpty="false"
                                           :isCopyButton="false"
                                           repeaterStyle="margin-top:5px;width:95%"
                                           name="attendances"
                                           :title="[__('First name'), __('Last name'), __('Organization')]">
                            <template #repeat="slotProps">
                                <td style="width:25%;"
                                    :style="slotProps.cellStyle">
                                    <FormInputText fieldName="first_name"
                                                   :inRepeater="true"
                                                   v-model:field="slotProps.record.first_name" />
                                </td>
                                <td style="width:35%"
                                    :style="slotProps.cellStyle">
                                    <FormInputText fieldName="last_name"
                                                   :inRepeater="true"
                                                   v-model:field="slotProps.record.last_name" />
                                </td>
                                <td style="width:40%"
                                    :style="slotProps.cellStyle">
                                    <FormInputText fieldName="organization"
                                                   :inRepeater="true"
                                                   v-model:field="slotProps.record.organization" />
                                </td>
                            </template>
                        </FormRepeaterTable>
                    </div>

                </TabPanel>
                <TabPanel v-if="crud.log"
                          value="2">
                    <ChangeLog :log="crud?.log" />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         :buttonsVisibility="buttonsVisibility"
                         :showAfterSave="true"
                         v-model:isFormDisabled="isFormDisabled"
                         v-model:autoSave="autoSave">
                <template #after-in-show="buttonProps">
                    <CustomButton name="printProtocol"
                                  :buttonProps="buttonProps"
                                  :buttonsVisibility="buttonsVisibility"
                                  :click="() => printProtocol()"
                                  style="margin:0 5px"
                                  severity="info">
                        {{ __('Print') }}
                    </CustomButton>
                </template>

            </FormButtons>
        </div>
    </v-form>
</template>

<script setup>
import { ref, onMounted } from 'vue'

import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import FormInputText from '@crud/FormInputText.vue'
import FormSelect from '@crud/FormSelect.vue'
import FormButtons from '@crud/FormButtons.vue'
import CustomButton from '@crud/CustomButton.vue'
import FormCalendar from '@crud/FormCalendar.vue'
import FormTextarea from '@crud/FormTextarea.vue'
import FormFileUploader from '@crud/FormFileUploader.vue'
import ChangeLog from '@crud/ChangeLog.vue'
import FormRepeaterTable from '@crud/FormRepeaterTable.vue'
import { __, getFromRoute, printReport } from '@pages/Helper/utils.js'
import { validation, initFormObj, onFilterService } from '@pages/Helper/crud_utils.js'

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
    customData: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(false)
const autoSave = ref(false)
const buttonsVisibility = ref(null);
const options = ["services"];
const select = ref(null);

const maxAgendaChars = 1000;


const crud = ref({})

// -----------------------------------------------------

onMounted(async () => {
    const paramsObj = { props, crud, isLoading, options, select, buttonsVisibility, $paramsToSelect: { 'protocol': 'A' } }
    await initFormObj(paramsObj)
})

// -----------------------------------------------------

const printProtocol = async () => {
    isLoading.value = true;

    if (!crud.value.id) {
        crud.value.id = (await getFromRoute('aprot-last-added', {}, 'get')).id
    }
    const response = await getFromRoute('aprot-print', { 'aprot_id': crud.value.id }, 'post')
    printReport(response)
    isLoading.value = false;
}

// --------------------------------------------------------------------------------------------------------------------------------------------

const agendaWasChanged = (value) => {
    if (value && value.length > maxAgendaChars) {
        crud.value.agenda = value.substring(0, maxAgendaChars);
    }
}

// -----------------------------------------------------
</script>

<style lang="scss" scoped></style>
